// Imports
import { Button, InputAdornment, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { GobikesApi } from "../../config/Apis";
import { Colors, DEFAULT_LOADER_STYLES } from "../../config/Constants";
import { getHubSelection } from "../../features/hubSelection";
import { getAuthCookie } from "../auth/AuthUtil";
import { nonNegativeValidationFn } from "../utils/ValidationUtils";
import { GbkFieldType, GobikesField } from "../utils/GobikesField";
import { DashboardLoader } from "../utils/LocalStateUtils";
import { AccessoryAvailability, AccessoryModel, AccessoryPrice, HubAccessoryModel } from "../utils/Models";
import { ArrowBack, Close, DoneAll } from "@mui/icons-material";

// Constants and interfaces
interface LocalState {
  loading?: boolean;
  error?: boolean;
  message?: string;
  accessoryClassData?: AccessoryModel[]
  accessoryClassMap?: { [k: number]: AccessoryModel }
}
const accessoryPackageToName: { [k in keyof AccessoryAvailability]: string } = {
  trip: 'Trip',
  halfDay: 'Half Day Price',
  fullDay: 'Full Day Price',
  week: 'Weekly Price',
  fortnight: '15 Days Price',
  month: '30 Days Price',
  quarterYear: '3 Month Package',
  halfYear: '6 Month Package',
  fullYear: '12 Month Package',
}

// Individual booking details
export function AccessoryDetails(props: {
  accessory: AccessoryModel,
  backFn: (reload: boolean) => void,
  drawerWidth: number,
  editMode: boolean,
  creating: boolean
}) {

  // Local states
  const [editMode, setEditMode] = useState(props.editMode);
  const [state, setState] = useState<LocalState>({});
  const [currentAccessoryData, setCurrentAccessoryData] = useState({ accessory: { ...props.accessory }, hubAccessory: JSON.parse(JSON.stringify(props.accessory.hub_accessories)) as HubAccessoryModel });

  // Parse state and props
  const authCookie = getAuthCookie();
  const selectedHub = useSelector(getHubSelection);
  const theme = useTheme();
  const onlyMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  // Load data on when component opens if creating
  useEffect(() => {
    if (!props.creating || state.accessoryClassData) return;
    setState({ loading: true });
    (async () => {
      try {
        const { data } = await GobikesApi.get(`/bikes/accessory-classes?notInHub=${selectedHub.id}`, { headers: { 'vendor-token': authCookie } });
        if (!data.data) {
          throw new Error('Failed to load bike class data!');
        }
        console.log(data.data);
        setState({ loading: false, accessoryClassData: data.data, accessoryClassMap: data.data.reduce((a: { [k: number]: AccessoryModel }, v: AccessoryModel) => ({ ...a, [v.id]: v }), {}) });
      } catch (e: any) {
        setState({ loading: false, error: true, message: e.message });
      }
    })();
  }, [props]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset current accessory data when edit mode changed
  useEffect(() => {
    if (!editMode) setCurrentAccessoryData({ accessory: { ...props.accessory }, hubAccessory: JSON.parse(JSON.stringify(props.accessory.hub_accessories)) });
  }, [editMode, props]);

  // Function to update current accessory state
  function updateCurrentAccessoryData(update: Partial<AccessoryModel>) {
    setCurrentAccessoryData({ ...currentAccessoryData, accessory: { ...currentAccessoryData.accessory, ...update } })
  }
  function updateCurrentHubAccessoryData(update: Partial<HubAccessoryModel>) {
    setCurrentAccessoryData({ ...currentAccessoryData, hubAccessory: { ...currentAccessoryData.hubAccessory, ...update } })
  }
  function updateCurrentHubAccessoryPrice(update: Partial<AccessoryPrice>) {
    setCurrentAccessoryData({ ...currentAccessoryData, hubAccessory: { ...currentAccessoryData.hubAccessory, prices: { ...currentAccessoryData.hubAccessory.prices, ...update } } })
  }
  function updateCurrentHubAccessoryAvailability(update: Partial<AccessoryAvailability>) {
    setCurrentAccessoryData({ ...currentAccessoryData, hubAccessory: { ...currentAccessoryData.hubAccessory, availability: { ...currentAccessoryData.hubAccessory.availability, ...update } } })
  }

  // Save accessory details
  async function saveAccessoryDetails() {

    // Parse input and check for errors
    const payload: any = {
      accessoryId: currentAccessoryData.hubAccessory.accessoryId,
      prices: currentAccessoryData.hubAccessory.prices,
      availability: currentAccessoryData.hubAccessory.availability
    };

    // Validate payload
    const missingPrices: string[] = [];
    for (const key of Object.keys(payload.availability)) {
      if (payload.availability[key] && (payload.prices[key] === null || payload.prices[key] === undefined)) missingPrices.push(key);
    }
    if (missingPrices.length) {
      setState({ error: true, message: `Add price or remove the checkbox for these fields - ${missingPrices.map(k => accessoryPackageToName[k as keyof AccessoryAvailability]).join(', ')}`, loading: false })
      return;
    }

    // Set loading mode and try to save accessory
    setState({ loading: true });
    try {
      await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/accessories`, payload, { headers: { 'vendor-token': authCookie } });
      setState({ error: false, message: 'Accessory edited successfully!', loading: false })
      props.backFn(true);
    } catch (e: any) {
      const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
      setState({ error: true, message: errorMessage, loading: false })
    }
  }

  // Return DOM
  if (state.error || state.loading)
    return (
      <DashboardLoader
        {...state}
        styles={DEFAULT_LOADER_STYLES}
        customIcon={<DoneAll className="icon" style={{ color: Colors.primary }} />}
        resetError={state.error ? () => setState({ ...state, error: false, loading: false }) : undefined}
        resetErrorText={'Dismiss'}
        resetErrorIcon={<Close />}
        resetErrorIconPrefix={true}
      />
    )
  else return (
    <Fragment>
      <div className="gbk-paper-container">

        {/* Empty space at top for phone screen */}
        <Fragment>
          <Box sx={{ display: { xs: 'block', md: 'none' }, height: { xs: '18px', sm: '26px' }, width: 1 }}><br /></Box>
        </Fragment>

        {/* Accessory details */}
        {renderAccessoryDetails()}<br /><br />

        {/* Charges */}
        {renderPricingDetails()}<br /><br /><br />

        <br />
      </div>

      {/* Action buttons */}
      <Box sx={{
        position: 'fixed',
        bottom: 0,
        left: { xs: 0, md: props.drawerWidth },
        width: { xs: '100vw', md: `calc(100vw - ${props.drawerWidth}px)` },
        padding: '20px 40px',
        paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
        background: '#fff',
        boxShadow: '0px -2px 6px #ddd',
        zIndex: 10
      }}>
        {
          props.creating ? (
            <Fragment>
              <Button className="thick-border" variant="contained" onClick={saveAccessoryDetails}>Create</Button>
              <Button className="thick-border" variant="outlined" sx={{ marginLeft: 2 }} onClick={e => props.backFn(true)}>Cancel</Button>
            </Fragment>
          ) : (
            editMode
              ? (
                <Fragment>
                  <Button className="thick-border" variant="contained" onClick={saveAccessoryDetails}>Save</Button>
                  <Button className="thick-border" variant="outlined" sx={{ marginLeft: 2 }} onClick={e => setEditMode(false)}>Cancel</Button>
                </Fragment>
              )
              : (
                <Fragment>
                  <Button className="thick-border" variant="contained" onClick={e => setEditMode(true)}>Edit</Button>
                </Fragment>
              )
          )
        }
      </Box>
    </Fragment >
  )

  /*
   * Render utility functions beyond this point 
   */

  // High level accessory details
  function renderAccessoryDetails() {
    return <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
      <div className="header">
        <Typography className="header-left" fontSize={18} fontWeight={500}>
          {onlyMediumScreen ? <span className="gbk-icon-span large clickable" onClick={() => props.backFn(true)}><ArrowBack />&nbsp;&nbsp;</span> : ''}
          Accessory Details</Typography>
      </div>
      <div className="content" >
        <div className="gbk-col" style={{ minWidth: 300 }}>
          {props.creating ? (
            <GobikesField
              type={GbkFieldType.SELECT}
              label={'Accessory Name'}
              selectOptions={(state.accessoryClassData || []).map(b => ({ value: b.id, display: `${b.name}` }))}
              editMode={true}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={currentAccessoryData.hubAccessory.accessoryId > 0 ? currentAccessoryData.hubAccessory.accessoryId : ''}
              updateFn={(v: number) => {
                updateCurrentAccessoryData({ ...(state && state.accessoryClassMap && { ...state.accessoryClassMap[v] }) })
                updateCurrentHubAccessoryData({ ...(state && state.accessoryClassMap && { accessoryId: state.accessoryClassMap[v].id }) })
              }}
            />
          ) : (
            <GobikesField
              type={GbkFieldType.STRING}
              label={'Accessory'}
              editMode={false}
              value={currentAccessoryData.accessory.name}
            />
          )}
          <br />
        </div>
        <div className="gbk-col" style={{ minWidth: 300 }}>
          <GobikesField
            type={GbkFieldType.UPLOAD}
            label={'Photo'}
            editMode={false}
          />
          <br />
        </div>
      </div>
    </Paper>
  }

  // Accessory charges details
  function renderPricingDetails() {
    return <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
      <div className="header">
        <Typography className="header-left" fontSize={18} fontWeight={500}>Charges</Typography>
      </div>
      <div className="content" >
        <Typography className="error-text">If trip package is enabled, it will override all other packages.<br /><br /></Typography>
        {
          ([
            { name: 'Trip Package', field: 'trip' },
            { name: 'Half Day Price', field: 'halfDay' },
            { name: 'Full Day Price', field: 'fullDay' },
            { name: 'Weekly Price', field: 'week' },
            { name: '15 Days Price', field: 'fortnight' },
            { name: '30 Days Price', field: 'month' },
            { name: '3 Month Package', field: 'quarterYear' },
            { name: '6 Month Package', field: 'halfYear' },
            { name: '12 Month Package', field: 'fullYear' },
          ] as {
            name: string,
            field: keyof AccessoryPrice,
          }[]).map((f, i) => (
            <Fragment key={i}>
              <Box>
                <Box className="gbk-col" sx={{ width: { xs: 160, md: 250 } }}>
                  <GobikesField
                    type={GbkFieldType.CHECKBOX}
                    label={f.name}
                    editMode={editMode}
                    disabled={currentAccessoryData.hubAccessory.availability.trip && f.field !== 'trip'}
                    value={currentAccessoryData.hubAccessory.availability[f.field]}
                    updateFn={(v: boolean) => {
                      updateCurrentHubAccessoryAvailability({ [f.field]: v });
                    }}
                  />
                </Box>
                <Box className="gbk-col" sx={{ width: { xs: 150, md: 200 } }}>
                  <GobikesField
                    type={GbkFieldType.NUMBER}
                    editMode={editMode}
                    value={currentAccessoryData.hubAccessory.prices[f.field] || 0}
                    updateFn={(v: number) => {
                      updateCurrentHubAccessoryPrice({ [f.field]: v });
                    }}
                    inputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    validateFn={nonNegativeValidationFn()}
                    displayPrefix='₹'
                  />
                  <br />
                </Box>
              </Box>
            </Fragment>
          ))
        }


        {/* <div className="gbk-col" style={{ minWidth: 300 }}>
          <GobikesField
            type={GbkFieldType.SELECT}
            label={'Accessory Type'}
            editMode={editMode}
            selectOptions={["DAILY", "TRIP"].map(a => ({ display: a, value: a.toString() }))}
            value={(currentAccessoryData.hubAccessory.accessoryType || '').toString()}
            updateFn={(v: AccessoryType) => {
              updateCurrentHubAccessoryData({ accessoryType: v })
            }}
            validateFn={nonNegativeValidationFn()}
          />
          <br />
        </div>
        <div className="gbk-col" style={{ minWidth: 300 }}>
          <GobikesField
            type={GbkFieldType.NUMBER}
            label={'Price'}
            editMode={editMode}
            value={currentAccessoryData.hubAccessory.price || 0}
            updateFn={(v: number) => {
              updateCurrentHubAccessoryData({ price: v })
            }}
            validateFn={nonNegativeValidationFn()}
            inputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
          />
          <br />
        </div> */}

      </div>
    </Paper>
  }
}